import { useState, useEffect, useCallback, useRef, MutableRefObject } from 'react';
import { Loader2, Building2, BookText, FileText, Sparkles, Check, AlertCircle, Phone, MessageSquare, Mail, Linkedin, X, CheckCircle } from 'lucide-react';
import { AboutCompanySection } from './AboutCompanySection';
import { PitchBookSection } from './PitchBookSection';
import { CompanyMaterialsSettings } from './CompanyMaterialsSettings';
import { SalesMaterialsBottomBar } from './SalesMaterialsBottomBar';
import { CompanyResearchOnboarding } from './CompanyResearchOnboarding';
import { ResearchProgressView } from './ResearchProgressView';
import { GenerateAllProgressSteps } from './GenerateAllProgressSteps';
import {
  getSalesMaterials,
  saveAboutCompany,
  savePitchBook,
  generateAllAgentScripts,
  startCompanyResearch,
  resetResearchStatus,
  getResearchStatus,
} from '../../services/salesMaterialsService';
import { getIcpConfig, saveIcpConfig, getIcpMasterData, resolveTargetAudienceStrings } from '../../services/icpService';
import { useAuth } from '../../contexts/AuthContext';
import { canEditSalesPlaybook } from '../../utils/permissions';
import type { IcpConfig } from '../../types/icp';
import { ICP_LIMITS } from '../../types/icp';
import type { SalesMaterials, AboutCompany, PitchBook, OnboardingInput, ResearchStatus } from '../../types/settings';
import { EMPTY_ABOUT_COMPANY, EMPTY_PITCH_BOOK } from '../../types/settings';

type ActiveTab = 'about' | 'pitch' | 'materials';

interface SalesMaterialsSettingsProps {
  onDirtyStateChange?: (dirty: boolean) => void;
  saveRef?: MutableRefObject<(() => Promise<void>) | null>;
  discardRef?: MutableRefObject<(() => void) | null>;
}

function isAboutCompanyEmpty(ac: AboutCompany): boolean {
  return (
    !ac.products_services &&
    !ac.usp_statement &&
    ac.target_audience.countries.length === 0 &&
    ac.target_audience.industries.length === 0 &&
    ac.target_audience.company_sizes.length === 0 &&
    ac.target_audience.decision_maker_titles.length === 0 &&
    (ac.competitors || []).length === 0 &&
    ac.pain_points.length === 0 &&
    (ac.solutions || []).length === 0 &&
    (ac.desires || []).length === 0 &&
    ac.benefits.length === 0 &&
    (ac.prospect_questions || []).length === 0
  );
}

const IN_PROGRESS_STATUSES: ResearchStatus[] = ['scraping_website', 'researching', 'generating_insights'];

export function SalesMaterialsSettings({ onDirtyStateChange, saveRef, discardRef }: SalesMaterialsSettingsProps) {
  const { user } = useAuth();
  const canEdit = canEditSalesPlaybook(user);
  const [activeTab, setActiveTab] = useState<ActiveTab>('about');
  const [materials, setMaterials] = useState<SalesMaterials | null>(null);
  const [originalMaterials, setOriginalMaterials] = useState<SalesMaterials | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalPhase, setModalPhase] = useState<'confirm' | 'progress' | 'success' | 'error'>('confirm');
  const [modalVisiblePhase, setModalVisiblePhase] = useState<'confirm' | 'progress' | 'success' | 'error'>('confirm');
  const [modalTransitioning, setModalTransitioning] = useState(false);
  const [genApiDone, setGenApiDone] = useState(false);
  const [genApiFailed, setGenApiFailed] = useState(false);
  const [genError, setGenError] = useState('');
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showResearchBanner, setShowResearchBanner] = useState(false);
  const [forceNormalView, setForceNormalView] = useState(false);
  const [icpConfig, setIcpConfig] = useState<IcpConfig | null>(null);
  const [originalIcpConfig, setOriginalIcpConfig] = useState<IcpConfig | null>(null);
  const [showTitleWarning, setShowTitleWarning] = useState(false);

  const loadMaterialsRef = useRef<(silent?: boolean) => Promise<void>>();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadMaterials();
    if (user?.client_id) {
      getIcpConfig(user.client_id).then(config => {
        if (mountedRef.current) {
          setIcpConfig(config);
          setOriginalIcpConfig(config ? JSON.parse(JSON.stringify(config)) : null);
        }
      });
    }
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (materials && originalMaterials) {
      const materialsChanged =
        JSON.stringify(materials.about_company) !== JSON.stringify(originalMaterials.about_company) ||
        JSON.stringify(materials.pitch_book) !== JSON.stringify(originalMaterials.pitch_book);
      const icpChanged = JSON.stringify(icpConfig) !== JSON.stringify(originalIcpConfig);
      const changed = materialsChanged || icpChanged;
      setHasChanges(changed);
      onDirtyStateChange?.(changed);
    }
  }, [materials, originalMaterials, icpConfig, originalIcpConfig]);

  useEffect(() => {
    return () => {
      onDirtyStateChange?.(false);
    };
  }, []);

  const loadMaterials = async (silent = false) => {
    if (!silent) setLoading(true);
    const data = await getSalesMaterials();
    if (data) {
      const normalized = {
        ...data,
        about_company: {
          ...EMPTY_ABOUT_COMPANY,
          ...data.about_company,
        },
        pitch_book: {
          ...EMPTY_PITCH_BOOK,
          ...data.pitch_book,
        },
        research_status: data.research_status || 'idle',
        research_error: data.research_error || null,
        onboarding_input: data.onboarding_input || null,
      };
      setMaterials(normalized);
      setOriginalMaterials(JSON.parse(JSON.stringify(normalized)));
    }
    if (!silent) setLoading(false);
  };

  loadMaterialsRef.current = loadMaterials;

  useEffect(() => {
    if (modalPhase === modalVisiblePhase) return;
    setModalTransitioning(true);
    const timer = setTimeout(() => {
      setModalVisiblePhase(modalPhase);
      setModalTransitioning(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [modalPhase, modalVisiblePhase]);

  const isResearchActive = materials ? IN_PROGRESS_STATUSES.includes(materials.research_status) : false;

  useEffect(() => {
    if (!isResearchActive) return;

    let stopped = false;

    const pollFn = async () => {
      if (stopped) return false;
      const { status, error } = await getResearchStatus();
      if (stopped) return false;

      if (status === 'completed') {
        setMaterials(prev => prev ? { ...prev, research_status: 'completed' } : prev);
        setTimeout(async () => {
          if (mountedRef.current) {
            setShowResearchBanner(true);
            await loadMaterialsRef.current?.(true);
            if (user?.client_id) {
              const freshIcp = await getIcpConfig(user.client_id);
              if (mountedRef.current) {
                setIcpConfig(freshIcp);
                setOriginalIcpConfig(freshIcp ? JSON.parse(JSON.stringify(freshIcp)) : null);
              }
            }
          }
        }, 1500);
        return true;
      }

      if (status === 'failed') {
        setMaterials(prev => prev ? {
          ...prev,
          research_status: 'failed',
          research_error: error,
        } : prev);
        return true;
      }

      setMaterials(prev => {
        if (!prev || prev.research_status === status) return prev;
        if (status === 'idle') return prev;
        return { ...prev, research_status: status };
      });

      return false;
    };

    pollFn();

    const interval = setInterval(async () => {
      const done = await pollFn();
      if (done) {
        stopped = true;
        clearInterval(interval);
      }
    }, 2500);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [isResearchActive]);

  useEffect(() => {
    if (
      materials?.research_status === 'completed' &&
      isAboutCompanyEmpty(materials.about_company)
    ) {
      const timeout = setTimeout(() => {
        if (mountedRef.current) {
          loadMaterialsRef.current?.(true);
        }
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [materials?.research_status, materials?.about_company]);

  const handleStartResearch = (input: OnboardingInput) => {
    if (materials) {
      setMaterials({
        ...materials,
        research_status: 'scraping_website',
        onboarding_input: input,
      });
    }

    startCompanyResearch(input).then(result => {
      if (!result.success) {
        setMaterials(prev => {
          if (!prev || prev.research_status === 'completed') return prev;
          return {
            ...prev,
            research_status: 'failed',
            research_error: result.error || 'Failed to start research',
          };
        });
      }
    }).catch(() => {
      setMaterials(prev => {
        if (!prev || prev.research_status === 'completed') return prev;
        return {
          ...prev,
          research_status: 'failed',
          research_error: 'Network error. Please check your connection and try again.',
        };
      });
    });
  };

  const handleSkipOnboarding = async () => {
    await resetResearchStatus();
    setForceNormalView(true);
    if (materials) {
      setMaterials({ ...materials, research_status: 'idle' });
    }
  };

  const handleAboutCompanyChange = (aboutCompany: AboutCompany) => {
    if (!materials) return;
    setMaterials({ ...materials, about_company: aboutCompany });
  };

  const handlePitchBookChange = (pitchBook: PitchBook) => {
    if (!materials) return;
    setMaterials({ ...materials, pitch_book: pitchBook });
  };

  const handleSave = async () => {
    if (!materials) return;

    if (icpConfig && icpConfig.title_ids.length < ICP_LIMITS.titles.min) {
      setShowTitleWarning(true);
      return;
    }

    setSaving(true);

    let success = true;
    let updatedMaterials = { ...materials };

    const icpChanged = user?.client_id && icpConfig && JSON.stringify(icpConfig) !== JSON.stringify(originalIcpConfig);

    if (icpChanged) {
      try {
        await saveIcpConfig(user!.client_id!, icpConfig!);
        const masterData = await getIcpMasterData();
        const resolved = resolveTargetAudienceStrings(icpConfig!, masterData);
        updatedMaterials = {
          ...updatedMaterials,
          about_company: {
            ...updatedMaterials.about_company,
            target_audience: resolved,
          },
        };
        setMaterials(updatedMaterials);
      } catch {
        success = false;
      }
    }

    const aboutChanged = JSON.stringify(updatedMaterials.about_company) !== JSON.stringify(originalMaterials?.about_company);
    if (aboutChanged) {
      const result = await saveAboutCompany(updatedMaterials.about_company);
      if (!result) success = false;
    }

    if (JSON.stringify(updatedMaterials.pitch_book) !== JSON.stringify(originalMaterials?.pitch_book)) {
      const result = await savePitchBook(updatedMaterials.pitch_book);
      if (!result) success = false;
    }

    if (success) {
      setSaved(true);
      setOriginalMaterials(JSON.parse(JSON.stringify(updatedMaterials)));
      setOriginalIcpConfig(icpConfig ? JSON.parse(JSON.stringify(icpConfig)) : null);
      setHasChanges(false);
      setTimeout(() => setSaved(false), 2000);
    }

    setSaving(false);
  };

  const handleDiscard = () => {
    if (originalMaterials) {
      setMaterials(JSON.parse(JSON.stringify(originalMaterials)));
      setIcpConfig(originalIcpConfig ? JSON.parse(JSON.stringify(originalIcpConfig)) : null);
      setHasChanges(false);
      onDirtyStateChange?.(false);
    }
  };

  useEffect(() => {
    if (saveRef) saveRef.current = handleSave;
    if (discardRef) discardRef.current = handleDiscard;
  });

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      if (hasChanges && !saving) {
        handleSave();
      }
    }
  }, [hasChanges, saving]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasChanges]);

  const isReadyToGenerate = (): boolean => {
    if (!materials) return false;
    const ac = materials.about_company;
    const pb = materials.pitch_book;
    return (
      !!ac.products_services &&
      !!ac.usp_statement &&
      (pb.hooks || []).length >= 3 &&
      (pb.elaborations || []).length >= 3 &&
      (pb.engaging_questions || []).length >= 3 &&
      (pb.qualifying_questions || []).length >= 2 &&
      (pb.objections || []).length >= 5
    );
  };

  const getMissingItems = (): string[] => {
    if (!materials) return [];
    const missing: string[] = [];
    const ac = materials.about_company;
    const pb = materials.pitch_book;
    if (!ac.products_services) missing.push('Products/Services');
    if (!ac.usp_statement) missing.push('USP Statement');
    const hooks = pb.hooks || [];
    const elaborations = pb.elaborations || [];
    const engagingQ = pb.engaging_questions || [];
    const qualifyingQ = pb.qualifying_questions || [];
    const objections = pb.objections || [];
    if (hooks.length < 3) missing.push(`Hooks (${hooks.length}/3)`);
    if (elaborations.length < 3) missing.push(`Elaborations (${elaborations.length}/3)`);
    if (engagingQ.length < 3) missing.push(`Engaging Questions (${engagingQ.length}/3)`);
    if (qualifyingQ.length < 2) missing.push(`Qualifying Questions (${qualifyingQ.length}/2)`);
    if (objections.length < 5) missing.push(`Objections (${objections.length}/5)`);
    return missing;
  };

  const handleGenerateAll = async () => {
    if (!materials) return;
    setModalPhase('progress');
    setGenApiDone(false);
    setGenApiFailed(false);
    setGenError('');
    setGenerating(true);
    setGenerationResult(null);

    try {
      const result = await generateAllAgentScripts(materials);

      if (result.success) {
        setGenApiDone(true);
        closeTimerRef.current = setTimeout(() => {
          setModalPhase('success');
          closeTimerRef.current = setTimeout(() => {
            setShowConfirmModal(false);
            resetModalState();
            setGenerationResult({
              success: true,
              message: 'Draft scripts generated for all agents. Visit each agent\'s settings page to review and approve.',
            });
            if (materials) {
              setMaterials({ ...materials, generation_status: 'generated' });
            }
            setGenerating(false);
          }, 2500);
        }, 1200);
      } else {
        setGenApiFailed(true);
        setGenError(result.error || 'Failed to generate scripts. Please try again.');
        setModalPhase('error');
        setGenerating(false);
      }
    } catch {
      setGenApiFailed(true);
      setGenError('Network error. Please check your connection and try again.');
      setModalPhase('error');
      setGenerating(false);
    }
  };

  const resetModalState = () => {
    setModalPhase('confirm');
    setModalVisiblePhase('confirm');
    setModalTransitioning(false);
    setGenApiDone(false);
    setGenApiFailed(false);
    setGenError('');
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleModalClose = () => {
    if (modalPhase === 'progress') return;
    setShowConfirmModal(false);
    resetModalState();
  };

  const handleGenerateRetry = () => {
    setGenError('');
    setGenApiFailed(false);
    setModalPhase('confirm');
  };

  const handleReResearch = async () => {
    await resetResearchStatus();
    setForceNormalView(false);
    if (materials) {
      setMaterials({
        ...materials,
        about_company: EMPTY_ABOUT_COMPANY,
        research_status: 'idle',
        research_error: null,
      });
    }
  };

  const showResearchProgress = materials ? (
    IN_PROGRESS_STATUSES.includes(materials.research_status) ||
    (materials.research_status === 'completed' && isAboutCompanyEmpty(materials.about_company))
  ) : false;

  const showOnboardingForm = materials ? (
    isAboutCompanyEmpty(materials.about_company) &&
    !forceNormalView &&
    !showResearchProgress &&
    (materials.research_status === 'idle' || materials.research_status === 'failed')
  ) : false;

  const isResearchRunning = materials ? IN_PROGRESS_STATUSES.includes(materials.research_status) : false;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
      </div>
    );
  }

  if (!materials) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="w-8 h-8 text-red-400" />
        <p className="text-sm text-gray-600">Failed to load sales playbook data. Please refresh the page.</p>
      </div>
    );
  }

  const tabs: { id: ActiveTab; label: string; icon: React.ElementType }[] = [
    { id: 'about', label: 'About Company', icon: Building2 },
    { id: 'pitch', label: 'Pitch Book', icon: BookText },
    { id: 'materials', label: 'Company Materials', icon: FileText },
  ];

  const completedCount = (): number => {
    const ac = materials.about_company;
    const pb = materials.pitch_book;
    let filled = 0;
    if (ac.products_services) filled++;
    if (ac.usp_statement) filled++;
    if ((ac.competitors || []).length > 0) filled++;
    if ((ac.solutions || []).length > 0) filled++;
    if ((ac.desires || []).length > 0) filled++;
    if ((ac.prospect_questions || []).length > 0) filled++;
    if (pb.hooks.length >= 3) filled++;
    if (pb.elaborations.length >= 3) filled++;
    if (pb.engaging_questions.length >= 3) filled++;
    if (pb.qualifying_questions.length >= 2) filled++;
    if (pb.objections.length >= 5) filled++;
    return filled;
  };

  const renderAboutTabContent = () => {
    if (showResearchProgress) {
      return (
        <ResearchProgressView
          currentStatus={materials.research_status}
          error={materials.research_error}
          onRetry={() => {
            if (materials) {
              setMaterials({ ...materials, research_status: 'idle', research_error: null });
            }
          }}
          onSkip={handleSkipOnboarding}
        />
      );
    }

    if (showOnboardingForm) {
      return (
        <CompanyResearchOnboarding
          onStartResearch={handleStartResearch}
          onSkip={handleSkipOnboarding}
          previousInput={materials.onboarding_input}
          previousError={materials.research_status === 'failed' ? materials.research_error : null}
        />
      );
    }

    return (
      <AboutCompanySection
        data={materials.about_company}
        onChange={handleAboutCompanyChange}
        onReResearch={handleReResearch}
        icpConfig={icpConfig}
        onIcpChange={setIcpConfig}
      />
    );
  };

  return (
    <div className="max-w-full">
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md shrink-0">
              <BookText className="w-6 h-6 text-[#1875FF]" />
            </div>
            <div>
              <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Sales Playbook</h2>
              <p className="text-gray-600">
                Define your sales playbook once. All AI agents will use this information to personalize their outreach.
              </p>
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => {
                  if (hasChanges) {
                    alert('Please save your changes before generating scripts.');
                    return;
                  }
                  resetModalState();
                  setShowConfirmModal(true);
                }}
                disabled={!isReadyToGenerate() || generating || hasChanges || isResearchRunning}
                title={!isReadyToGenerate() ? `Missing: ${getMissingItems().join(', ')}` : ''}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-none"
              >
                {generating ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Sparkles className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">{generating ? 'Generating...' : 'Generate All Scripts'}</span>
              </button>
            </div>
          )}
        </div>

        {!canEdit && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">View Only</p>
              <p className="text-xs text-blue-800">
                Only admins can edit the sales playbook. Contact your admin to make changes.
              </p>
            </div>
          </div>
        )}

        {showResearchBanner && (
          <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-white to-[#05E0D1]/8 border-2 border-[#05E0D1]/25 shadow-lg flex items-start gap-3">
            <div className="p-1.5 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg shrink-0">
              <Check className="w-4 h-4 text-[#05E0D1]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Company profile auto-generated</p>
              <p className="text-sm text-gray-600 mt-0.5">Review each section and make any adjustments before generating agent scripts.</p>
            </div>
            <button
              onClick={() => setShowResearchBanner(false)}
              className="p-1.5 hover:bg-black/5 rounded-lg transition-colors shrink-0"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        )}

        {generationResult && (
          <div className={`mt-4 p-5 rounded-xl flex items-start gap-4 transition-all duration-300 ${
            generationResult.success
              ? 'bg-gradient-to-br from-white to-[#05E0D1]/8 border-2 border-[#05E0D1]/25 shadow-lg'
              : 'bg-gradient-to-br from-white to-red-50 border-2 border-red-200/50 shadow-lg'
          }`}>
            {generationResult.success ? (
              <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg shrink-0">
                <Check className="w-5 h-5 text-[#05E0D1]" />
              </div>
            ) : (
              <div className="p-2 bg-red-50 rounded-lg shrink-0">
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${generationResult.success ? 'text-gray-900' : 'text-red-800'}`}>
                {generationResult.success ? 'Scripts Generated Successfully' : 'Generation Failed'}
              </p>
              <p className={`text-sm mt-1 ${generationResult.success ? 'text-gray-600' : 'text-red-600'}`}>
                {generationResult.message}
              </p>
              {generationResult.success && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {[
                    { icon: Phone, label: 'Call Agent', hash: '#call-agent' },
                    { icon: MessageSquare, label: 'WhatsApp Agent', hash: '#whatsapp-agent' },
                    { icon: Mail, label: 'Email Agent', hash: '#email-agent' },
                    { icon: Linkedin, label: 'LinkedIn Agent', hash: '#linkedin-agent' },
                  ].map(agent => (
                    <a
                      key={agent.hash}
                      href={agent.hash}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1875FF]/10 text-[#1875FF] rounded-lg text-xs font-medium hover:bg-[#1875FF]/20 hover:shadow-sm transition-all duration-200"
                    >
                      <agent.icon className="w-3.5 h-3.5" />
                      Review {agent.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setGenerationResult(null)}
              className="flex-shrink-0 p-1.5 hover:bg-black/5 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        )}
      </div>

      <div className="flex border-b border-gray-200 mb-6">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm transition-all duration-300 ${
                isActive
                  ? 'font-semibold text-[#1875FF] bg-gradient-to-b from-[#1875FF]/5 to-transparent'
                  : 'font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className={`w-4 h-4 transition-colors duration-300 ${isActive ? 'text-[#1875FF]' : 'text-gray-400'}`} />
              {tab.label}
              {tab.id === 'about' && isResearchRunning && !isActive && (
                <span className="w-2 h-2 rounded-full bg-[#1875FF] animate-pulse" />
              )}
              {isActive && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-t-full" />
              )}
            </button>
          );
        })}
      </div>

      {isResearchRunning && activeTab !== 'about' && (
        <div className="mb-5 p-3.5 rounded-xl bg-[#1875FF]/5 border border-[#1875FF]/15 flex items-center gap-3">
          <Loader2 className="w-4 h-4 text-[#1875FF] animate-spin shrink-0" />
          <p className="text-sm text-[#1875FF] font-medium">
            Company research is running in the background. Wait a few more seconds until it's done.
          </p>
        </div>
      )}

      <div className={!canEdit ? 'pointer-events-none opacity-75' : ''}>
        {activeTab === 'about' && renderAboutTabContent()}

        {activeTab === 'pitch' && (
          <PitchBookSection
            data={materials.pitch_book}
            aboutCompany={materials.about_company}
            onChange={handlePitchBookChange}
          />
        )}

        {activeTab === 'materials' && (
          <CompanyMaterialsSettings />
        )}
      </div>

      {canEdit && activeTab !== 'materials' && !showOnboardingForm && !showResearchProgress && (
        <SalesMaterialsBottomBar
          completedCount={completedCount()}
          totalCount={11}
          hasChanges={hasChanges}
          saving={saving}
          saved={saved}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      )}

      {showTitleWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-[fadeIn_150ms_ease-out]"
            onClick={() => setShowTitleWarning(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 animate-[scaleIn_200ms_ease-out]">
            <button
              onClick={() => setShowTitleWarning(false)}
              className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X size={16} className="text-gray-400" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-[#1875FF]/10 rounded-xl">
                <AlertCircle className="w-5 h-5 text-[#1875FF]/70" />
              </div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">More Titles Needed</h3>
            </div>

            <p className="text-sm text-gray-500 mb-4 leading-relaxed">
              Please select at least <span className="font-bold text-[#1875FF]">{ICP_LIMITS.titles.min} decision-maker titles</span> in
              your Target Audience / ICP section before saving.
            </p>

            <div className="flex items-center gap-3 p-3 bg-[#1875FF]/5 border border-[#1875FF]/15 rounded-xl mb-5">
              <div className="text-2xl font-extrabold text-[#1875FF]">
                {icpConfig?.title_ids.length || 0}/{ICP_LIMITS.titles.min}
              </div>
              <div className="text-xs text-[#1875FF]/80 leading-snug">
                titles currently selected. Add {ICP_LIMITS.titles.min - (icpConfig?.title_ids.length || 0)} more to continue.
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-5">
              More titles significantly improve lead discovery results by giving the search engine more matching criteria to work with.
            </p>

            <button
              onClick={() => {
                setShowTitleWarning(false);
                setActiveTab('about');
              }}
              className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-xl hover:shadow-lg hover:shadow-[#1875FF]/20 transition-all"
            >
              Go to Target Audience
            </button>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-6 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-extrabold text-gray-900 font-display">Generate All Agent Scripts</h3>
              </div>
              <button
                onClick={handleModalClose}
                disabled={modalVisiblePhase === 'progress'}
                className="text-gray-400 hover:text-[#1875FF] transition-colors disabled:opacity-30 disabled:cursor-not-allowed p-2 hover:bg-white rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-hidden" style={{ minHeight: 340 }}>
              <div className={`h-full transition-opacity duration-250 ${modalTransitioning ? 'opacity-0' : 'opacity-100'}`}>
                {modalVisiblePhase === 'confirm' && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      This will generate draft scripts for all 4 agents based on your Sales Playbook:
                    </p>
                    <div className="space-y-2 mb-6">
                      {[
                        { icon: Phone, label: 'Call Agent - 1st Call, Follow Up Call, Objection Handling Scripts' },
                        { icon: MessageSquare, label: 'WhatsApp Agent - 16 WhatsApp Messages' },
                        { icon: Mail, label: 'Email Agent - 14 Emails' },
                        { icon: Linkedin, label: 'LinkedIn Agent - 6 LinkedIn Messages' },
                      ].map((agent, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <agent.icon className="w-4 h-4 text-[#1875FF] mt-0.5 shrink-0" />
                          <span>{agent.label}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-[#1875FF] bg-[#1875FF]/5 border border-[#1875FF]/15 rounded-lg px-3 py-2 mb-6">
                      Generated scripts will be saved as drafts. You can review and approve each one individually in the agent settings pages.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleModalClose}
                        className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleGenerateAll}
                        className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-xl hover:shadow-lg transition-all"
                      >
                        Generate Scripts
                      </button>
                    </div>
                  </div>
                )}

                {modalVisiblePhase === 'progress' && (
                  <div className="flex flex-col items-center justify-center" style={{ minHeight: 310 }}>
                    <GenerateAllProgressSteps apiDone={genApiDone} apiFailed={genApiFailed} />
                  </div>
                )}

                {modalVisiblePhase === 'success' && (
                  <div className="flex flex-col items-center justify-center" style={{ minHeight: 310 }}>
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle className="text-[#05E0D1]" size={32} />
                    </div>
                    <h4 className="text-lg font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1] font-display">Scripts Generated Successfully!</h4>
                    <p className="text-sm text-gray-500 mt-2 text-center max-w-xs">Draft scripts have been saved for all 4 agents. Review and approve them in settings.</p>
                  </div>
                )}

                {modalVisiblePhase === 'error' && (
                  <div className="flex flex-col items-center justify-center" style={{ minHeight: 310 }}>
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-red-50 mb-4">
                      <AlertCircle className="w-7 h-7 text-red-500" />
                    </div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">Something went wrong</h4>
                    <p className="text-sm text-gray-500 mb-6 text-center max-w-xs">{genError || 'An unexpected error occurred.'}</p>
                    <button
                      onClick={handleGenerateRetry}
                      className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-xl hover:shadow-lg transition-all"
                    >
                      Try Again
                    </button>
                    <button
                      onClick={handleModalClose}
                      className="text-sm text-gray-400 hover:text-gray-600 transition-colors mt-3"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
